/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { Action } from '@reduxjs/toolkit';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type {
  ScopeName,
  PluginManager,
  ScopeStateMap,
  ScopeEventMap,
} from '@opengeoweb/plugin-interface';

/**
 * Hook to subscribe to state changes for a scope
 */
export function useScopeState<T extends ScopeName>(
  pluginBridge: PluginManager,
  scope: T,
): ScopeStateMap[T] | undefined {
  const [state, setState] = useState<ScopeStateMap[T] | undefined>(() =>
    pluginBridge.getState(scope),
  );

  useEffect(() => {
    const unsubscribe = pluginBridge.subscribeToState(scope, (newState) => {
      setState(newState);
    });

    return unsubscribe;
  }, [pluginBridge, scope]);

  return state;
}

/**
 * Hook to send events for a scope
 */
export function useScopeEvent<T extends ScopeName>(
  pluginBridge: PluginManager,
  scope: T,
): (event: ScopeEventMap[T]) => void {
  const sendEvent = useCallback(
    (event: ScopeEventMap[T]) => {
      pluginBridge.sendEvent(scope, event);
    },
    [pluginBridge, scope],
  );

  return sendEvent;
}

// Returns true if event matches the bridge’s current state (no-op), so useSendOnReduxChange skips it and avoids plugin → Redux → plugin echoes.
export type IsEventRoundTrip<T extends ScopeName> = (
  event: ScopeEventMap[T],
  bridgeState: ScopeStateMap[T] | undefined,
) => boolean;

export function useSendOnReduxChange<T extends ScopeName, R>(
  pluginBridge: PluginManager,
  scope: T,
  reduxState: R,
  toEvent: (state: R) => ScopeEventMap[T] | null,
  enabled = true,
  isEventRoundTrip?: IsEventRoundTrip<T>,
): void {
  const sendEvent = useScopeEvent(pluginBridge, scope);
  const lastSentValueRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const event = toEvent(reduxState);
    if (!event) {
      return;
    }

    // If the bridge already has this value, the Redux change is an echo
    // of an event this plugin just sent. Skip to avoid looping it back.
    if (isEventRoundTrip?.(event, pluginBridge.getState(scope))) {
      return;
    }

    // Skip if we'd send the same value
    const eventString = JSON.stringify(event);
    if (eventString === lastSentValueRef.current) {
      return;
    }

    lastSentValueRef.current = eventString;
    sendEvent(event);
  }, [
    reduxState,
    enabled,
    sendEvent,
    toEvent,
    pluginBridge,
    scope,
    isEventRoundTrip,
  ]);
}

export function useDispatchOnScopeChange<T extends ScopeName>(
  pluginBridge: PluginManager,
  scope: T,
  actionCreator: (state: ScopeStateMap[T]) => Action | Action[] | null,
  enabled = true,
): void {
  const dispatch = useDispatch();
  const state = useScopeState(pluginBridge, scope);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (state === undefined) {
      return;
    }

    const actions = actionCreator(state);
    if (!actions) {
      return;
    }

    const actionArray = Array.isArray(actions) ? actions : [actions];
    actionArray.forEach(dispatch);
  }, [state, enabled, dispatch, actionCreator]);
}
